<template>
  <div>
    <div v-for="key in keys" :key="key" class="mb-3">
      <div class="pd-Cart_VariantHeader mb-1">{{ key }}</div>
      <ul
        v-if="propertiesForKey(key).length <= 12"
        class="pd-Cart_VariantList mb-n1 variant-option-values"
      >
        <PropertyButton
          v-for="property in propertiesForKey(key)"
          :key="`${property.key}-${property.value}`"
          :identifier="identifier"
          :property="property"
        />
      </ul>
      <div v-else class="row">
        <div class="col-md-16">
          <PropertySelect
            :properties="propertiesForKey(key)"
            :property-key="key"
            :reset_filter_text="reset_filter_text"
            :please_choose_text="please_choose_text"
          />
        </div>
      </div>
    </div>
    <p
      v-if="chosenVariant && variantDetails.count_on_hand === 0"
      class="text-small font-weight-bold alert alert-danger text-center"
    >
      {{ missing_count_on_hand_text }}
    </p>
    <alert
      v-if="chosenVariant && (variantDetails.count_on_hand > 0 && variantDetails.count_on_hand < 3)"
      variant="warning"
      class="mb-1 text-sm text-center"
    >
      {{ low_count_on_hand_text }}
    </alert>
  </div>
</template>

<script>
import PropertyButton from "./VariantPickerPropertyButton";
import PropertySelect from "./VariantPickerPropertySelect";
import Alert from "./Alert";
import compareProperties from "../mixins/compareProperties";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import storeTemplate from "../store/modules/variantPicker";
import { mapActions } from "vuex";

export default {
  name: "VariantPicker",
  components: {
    Alert,
    PropertyButton,
    PropertySelect,
  },
  mixins: [compareProperties],
  props: {
    identifier: {
      type: String,
      default: "",
    },
    variant_properties: {
      default: "",
      type: String,
    },
    missing_count_on_hand_text: {
      required: true,
      type: String,
    },
    low_count_on_hand_text: {
      required: true,
      type: String,
    },
    please_choose_text: {
      required: true,
      type: String,
    },
    reset_filter_text: {
      required: true,
      type: String,
    },
    preselectedProperties: {
      required: true,
      type: Array,
    },
  },
  computed: {
    selectedProperties() {
      return this.$store.getters[`variantPicker${this.identifier}/selectedProperties`];
    },
    variants() {
      return this.$store.getters[`variantPicker${this.identifier}/allProductVariants`];
    },
    chosenVariant() {
      return this.$store.getters[`variantPicker${this.identifier}/chosenVariant`];
    },
    keys() {
      const nestedKeys = this.variants.map(variant =>
        variant.properties.map(property => property.key)
      );

      const keys = Array.prototype.concat.apply([], nestedKeys);

      return [...new Set(keys)];
    },
    variantDetails() {
      return this.variants.find(variant => variant.id === this.chosenVariant);
    },
  },
  watch: {
    selectedProperties() {
      this.selectPropertyIfSingleVariantMatching();
      this.filterChosenVariant();
    },
  },
  created() {
    if (this.$store._modules.root._children[`variantPicker${this.identifier}`]) return;
    this.$store.registerModule(`variantPicker${this.identifier}`, storeTemplate);
  },
  mounted() {
    const variants = JSON.parse(this.variant_properties);

    this.$store.dispatch(`variantPicker${this.identifier}/setProductVariants`, variants);

    if (variants.length === 1) {
      this.$store.dispatch(`variantPicker${this.identifier}/setChosenVariant`, variants[0].id);
    }

    this.preselectedProperties.forEach(property => this.setSelectedProperties(property));
  },
  methods: {
    setSelectedProperties(property) {
      this.$store.dispatch(`variantPicker${this.identifier}/setSelectedProperties`, property);
    },
    propertiesForKey(key) {
      const properties = this.variants
        .map(variant => variant.properties.find(property => property.key === key))
        .filter(prop => prop != undefined);

      const sorted_properties = orderBy(properties, ["sort"], ["asc"]);
      return uniqBy(sorted_properties, property => property.value);
    },
    filterChosenVariant() {
      const selectedVariant = this.variants.find(variant => {
        const variantProperties = variant.properties;
        const selectedVariantProperties = variantProperties.map(variantProperty => {
          const isSelected = this.selectedProperties.some(selectedProperty =>
            this.compareProperties(selectedProperty, variantProperty)
          );

          return isSelected;
        });

        return selectedVariantProperties.every(property => property === true);
      });

      this.$store.dispatch(
        `variantPicker${this.identifier}/setChosenVariant`,
        selectedVariant ? selectedVariant.id : null
      );
    },
    selectPropertyIfSingleVariantMatching() {
      const variantsPossible = this.variants.filter(variant =>
        variant.properties.some(variantProperty =>
          this.selectedProperties.some(selectedProperty =>
            this.compareProperties(selectedProperty, variantProperty)
          )
        )
      );

      if (variantsPossible.length === 1) {
        const propertiesToSelect = variantsPossible[0].properties.filter(
          variantProperty =>
            !this.selectedProperties.some(selectedProperty =>
              this.compareProperties(selectedProperty, variantProperty)
            )
        );

        propertiesToSelect.forEach(propertyToSelect => {
          this.setSelectedProperties(propertyToSelect);
        });
      }
    },
  },
};
</script>
